import React, { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import { Stars, Billboard } from "@react-three/drei";
import * as THREE from "three";
import EarthBody from "./EarthBody";
import MoonBody from "./MoonBody";
import { SUN_POS, EARTH_POS, SHADOW, MOON_DIST, moonOrbitPoint } from "../lib/eclipse";

const shadowDir = SHADOW.dir.clone();

function radialTexture(stops) {
  const c = document.createElement("canvas");
  c.width = c.height = 256;
  const ctx = c.getContext("2d");
  const g = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
  stops.forEach(([o, col]) => g.addColorStop(o, col));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 256, 256);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

function ShadowCones() {
  // Shadow cone visuals fully removed per design: it must not dim the Earth at all.
  // The Moon still turns blood-red — that is driven by orbital coverage, not this mesh.
  return null;
}

function Sun() {
  const core = useRef();
  const corona = useRef();
  const flare = useRef();
  const glowTex = useMemo(() => radialTexture([[0, "rgba(255,244,214,1)"], [0.25, "rgba(255,190,120,0.9)"], [0.6, "rgba(255,120,60,0.25)"], [1, "rgba(255,90,40,0)"]]), []);
  const flareTex = useMemo(() => radialTexture([[0, "rgba(255,230,190,0.9)"], [0.5, "rgba(255,170,110,0.3)"], [1, "rgba(255,140,90,0)"]]), []);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const heat = 1 + Math.sin(t * 1.6) * 0.03 + Math.sin(t * 4.3) * 0.012; // heat pulse
    if (core.current) core.current.scale.setScalar(heat);
    if (corona.current) corona.current.material.opacity = 0.22 + Math.sin(t * 1.6) * 0.05;
    if (flare.current) flare.current.rotation.z = Math.sin(t * 0.4) * 0.15;
  });

  // lens-flare dots along the sun -> scene-centre axis
  const dir = EARTH_POS.clone().sub(SUN_POS).normalize();
  const dots = [
    { d: 5, s: 1.2, o: 0.35 }, { d: 9, s: 0.6, o: 0.22 }, { d: 14, s: 0.9, o: 0.26 },
    { d: 19, s: 0.5, o: 0.16 }, { d: 26, s: 0.8, o: 0.18 },
  ];

  return (
    <group>
      <group position={SUN_POS.toArray()}>
        <mesh ref={core}>
          <sphereGeometry args={[2.5, 64, 64]} />
          <meshBasicMaterial color={"#fff3cf"} toneMapped={false} />
        </mesh>
        <Billboard>
          <mesh ref={corona} scale={11}>
            <planeGeometry args={[1, 1]} />
            <meshBasicMaterial map={glowTex} transparent opacity={0.25} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
          </mesh>
        </Billboard>
        <pointLight intensity={4.2} distance={0} decay={0} color={"#fff2d6"} />
      </group>
      {/* lens flare chain */}
      <group ref={flare}>
        {dots.map((f, i) => (
          <Billboard key={i} position={SUN_POS.clone().addScaledVector(dir, f.d).toArray()}>
            <mesh scale={f.s * 3}>
              <planeGeometry args={[1, 1]} />
              <meshBasicMaterial map={flareTex} transparent opacity={f.o} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
          </Billboard>
        ))}
      </group>
    </group>
  );
}

function OrbitPath() {
  const geo = useMemo(() => {
    const pts = [];
    for (let i = 0; i <= 128; i++) pts.push(moonOrbitPoint((i / 128) * Math.PI * 2, 0));
    return new THREE.BufferGeometry().setFromPoints(pts);
  }, []);
  return (
    <line geometry={geo}>
      <lineBasicMaterial color={"#5a7fbf"} transparent opacity={0.16} depthWrite={false} />
    </line>
  );
}

function MovingMoon({ simRef, controlsRef }) {
  const grp = useRef();
  const redLight = useRef();
  useFrame(() => {
    const s = simRef.current;
    if (s && grp.current) grp.current.position.copy(s.moonPos);
    if (s && redLight.current) redLight.current.intensity = 0.3 + s.coverage * 1.4;
  });
  return (
    <group ref={grp}>
      <MoonBody simRef={simRef} radius={0.9} controlsRef={controlsRef} />
      <pointLight ref={redLight} color={"#ff6a3d"} intensity={0.35} distance={14} />
    </group>
  );
}

// Dynamic eclipse lighting: bright normally, dims as the Moon covers the Earth,
// and a deep-red blood-moon fill light is cast toward the Earth at totality.
function LightingController({ simRef, dirRef, ambRef, redRef }) {
  useFrame(() => {
    const s = simRef.current;
    if (!s) return;
    const cov = s.coverage;
    if (dirRef.current) dirRef.current.intensity = 3.9 - 0.5 * cov;
    if (ambRef.current) ambRef.current.intensity = 0.85 - 0.05 * cov;
    if (redRef.current) {
      redRef.current.intensity = cov * 0.85;
      redRef.current.color.setRGB(
        0.6 + s.danjonRgb[0] * 0.4,
        s.danjonRgb[1] * 0.6,
        s.danjonRgb[2] * 0.5
      );
    }
  });
  return null;
}

export default function AerialScene({ simRef, controlsRef, bortle, onEarthClick }) {
  const dirRef = useRef();
  const ambRef = useRef();
  const redRef = useRef();
  return (
    <>
      <color attach="background" args={["#02030a"]} />
      <ambientLight ref={ambRef} intensity={0.85} />
      <directionalLight ref={dirRef} position={SUN_POS.toArray()} intensity={3.4} color={"#fff4e0"} />
      <pointLight ref={redRef} position={[7, 1, 3]} distance={40} decay={0} color={"#ff5a2a"} intensity={0} />
      <Stars radius={120} depth={60} count={bortle <= 3 ? 6000 : 1800} factor={4} saturation={0} fade speed={0.4} />

      <Sun />
      <EarthBody controlsRef={controlsRef} simRef={simRef} onEarthClick={onEarthClick} />
      <ShadowCones />
      <OrbitPath />
      <MovingMoon simRef={simRef} controlsRef={controlsRef} />
      <LightingController simRef={simRef} dirRef={dirRef} ambRef={ambRef} redRef={redRef} />
    </>
  );
}
